import type { PrototypeRuntime } from "./prototypeTypes";
import type { BrowsePage, PublicTopic } from "./browseTypes";
import { PrototypeApiError } from './apiTransport';

export type BrowseSort = 'default' | 'trending' | 'newest';

/** Default ordering for the Browse surface. `default` remains a legacy catalog mode only. */
export const DEFAULT_BROWSE_SORT = 'newest' as const;

/** Empty-result fallback returned when a browse API call fails. */
function emptyBrowsePage(): BrowsePage {
  return { topics: [], totalTopics: 0, totalMinutes: 0, newThisWeek: 0, hasMore: false, nextCursor: null, error: "fetch" };
}

export async function loadBrowsePage(runtime: PrototypeRuntime, filter: string, cursor: string | null, search?: string, sort: BrowseSort = 'default'): Promise<BrowsePage> {
  if (filter === "recent") return safeTopicRecent(runtime, cursor, search);
  if (filter === "recommended") return safeTopicRecommended(runtime, cursor, search);
  if (filter === "favorites") return safeFavoritesWithTopics(runtime, cursor, search);
	// Ranking snapshots intentionally contain public topics only and can lag a
	// publication by one refresh interval. Search is a lookup operation, not a
	// ranking operation: use the live catalog so a newly published topic is
	// immediately discoverable and a signed-in owner can find only their own
	// private_ai topics. Empty-search New/Trending remains snapshot-backed and
	// therefore never adds private AI Create topics to the default browse feed.
	if (search?.trim()) return safeTopicCatalog(runtime, filter === 'all' ? undefined : filter, cursor, search);
	if (sort === 'trending') return safeTopicTrending(runtime, filter === 'all' ? undefined : filter, cursor, search);
	if (sort === 'newest') return safeTopicNewest(runtime, filter === 'all' ? undefined : filter, cursor, search);
  if (filter === "all") return safeTopicCatalog(runtime, undefined, cursor, search);
  return safeTopicCatalog(runtime, filter, cursor, search);
}

async function safeTopicNewest(runtime: PrototypeRuntime, category: string | undefined, cursor: string | null, search?: string): Promise<BrowsePage> {
	try {
		const r = (await runtime.api.topicNewest({ limit: 30, cursor, category, search })) as unknown as BrowsePage;
		return { ...r, topics: r.topics ?? [], totalTopics: r.totalTopics ?? 0, totalMinutes: r.totalMinutes ?? 0, newThisWeek: r.newThisWeek ?? 0 };
	} catch (error) {
		console.warn('[browseData] topicNewest failed', error);
		if (error instanceof PrototypeApiError && error.status === 410) return { ...emptyBrowsePage(), error: 'ranking_expired' };
		// The dedicated New route being absent means this deployment is misconfigured
		// or the feature is disabled. It is not evidence that the public collection
		// is empty, so preserve the recoverable unavailable state.
		if (error instanceof PrototypeApiError && error.status === 404) return { ...emptyBrowsePage(), error: 'newest_unavailable' };
		return { ...emptyBrowsePage(), error: 'newest_unavailable' };
	}
}

async function safeTopicTrending(runtime: PrototypeRuntime, category: string | undefined, cursor: string | null, search?: string): Promise<BrowsePage> {
	try {
		const r = (await runtime.api.topicTrending({ limit: 20, cursor, category, search })) as unknown as BrowsePage;
		return { ...r, topics: r.topics ?? [], totalTopics: r.totalTopics ?? 0, totalMinutes: r.totalMinutes ?? 0, newThisWeek: r.newThisWeek ?? 0 };
	} catch (error) {
		console.warn('[browseData] topicTrending failed', error);
		if (error instanceof PrototypeApiError && error.status === 410) return { ...emptyBrowsePage(), error: 'ranking_expired' };
		return { ...emptyBrowsePage(), error: 'trending_unavailable' };
	}
}

async function safeTopicRecent(runtime: PrototypeRuntime, cursor: string | null, search?: string): Promise<BrowsePage> {
  try {
    const r = (await runtime.api.topicRecent({ limit: 20, cursor, search })) as unknown as BrowsePage;
    return { ...r, topics: r.topics ?? [], totalTopics: r.totalTopics ?? 0, totalMinutes: r.totalMinutes ?? 0, newThisWeek: r.newThisWeek ?? 0 };
  } catch (error) {
    console.warn("[browseData] topicRecent failed (degrades to empty page)", error);
    return emptyBrowsePage();
  }
}

async function safeTopicRecommended(runtime: PrototypeRuntime, cursor: string | null, search?: string): Promise<BrowsePage> {
  try {
    const r = (await runtime.api.topicRecommended({ limit: 20, cursor, search })) as unknown as BrowsePage;
    return { ...r, topics: r.topics ?? [], totalTopics: r.totalTopics ?? 0, totalMinutes: r.totalMinutes ?? 0, newThisWeek: r.newThisWeek ?? 0 };
  } catch (error) {
    console.warn("[browseData] topicRecommended failed (degrades to empty page)", error);
    return emptyBrowsePage();
  }
}

async function safeTopicCatalog(runtime: PrototypeRuntime, category?: string, cursor: string | null = null, search?: string): Promise<BrowsePage> {
  try {
    const r = (await runtime.api.topicCatalog({ limit: 20, cursor, category, search })) as unknown as BrowsePage;
    return { ...r, topics: r.topics ?? [], totalTopics: r.totalTopics ?? 0, totalMinutes: r.totalMinutes ?? 0, newThisWeek: r.newThisWeek ?? 0 };
  } catch (error) {
    console.warn("[browseData] topicCatalog failed (degrades to empty page)", error);
    return emptyBrowsePage();
  }
}

/**
 * Single-call replacement for the legacy two-step pattern
 * (listFavorites slugs → topicCatalog enrichment). The server
 * does the enrichment inline when ?details=1 is passed.
 */
async function safeFavoritesWithTopics(runtime: PrototypeRuntime, cursor: string | null, search?: string): Promise<BrowsePage> {
  try {
    const resp = (await runtime.api.listFavoritesWithTopics({ limit: 20, cursor, search })) as BrowsePage & { topics?: PublicTopic[]; totalFavorites?: number; favorites?: unknown[] };
    return {
      ...resp,
      topics: resp.topics ?? [],
      totalTopics: resp.totalFavorites ?? resp.favorites?.length ?? 0,
      totalFavorites: resp.totalFavorites ?? resp.favorites?.length ?? 0,
      newThisWeek: 0,
      totalMinutes: 0,
    };
  } catch (error) {
    console.warn("[browseData] listFavoritesWithTopics failed (degrades to empty page)", error);
    return { topics: [], totalTopics: 0, totalFavorites: 0, newThisWeek: 0, totalMinutes: 0, hasMore: false, nextCursor: null, error: "fetch" };
  }
}
